import { DateItem } from './Item';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AtendentesService } from '../../../atendentes/Service';
import { Loading } from '../../../../components/Loading';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { AgendaProvider } from '../ContextAgenda';
import { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './Calendar.css';
import ptBR from 'date-fns/locale/pt-BR';
import { Locale } from 'date-fns';
import moment from 'moment';
import { useFilterApp } from '../../../../contexts/FilterApp';
import { useMediaQuery } from 'react-responsive';
import { FilaEsperaService } from '../../../fila-espera/Service';
import WaitlistAlertModal from '../../../fila-espera/estabelecimento/WaitlistAlertModal';
import FilterAgenda from '../components/AgendaFilter';
import { X } from 'lucide-react';

const locale = (ptBR as unknown) as Locale;
registerLocale('pt-BR', locale);

const atendentesService = new AtendentesService();
const filaEsperaService = new FilaEsperaService();

export interface AgendaEstabelecimentoDate {
    date: string;
    total: number;
    agora: {
        horario: string;
        cliente?: string;
        servico: string;
    };
    proximo: {
        horario: string;
        cliente?: string;
        servico: string;
    };
}

export const AgendaEstabelecimentoList = () => {
    const { user } = useAuthContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const { attendantId, date: selectedDate } = useFilterApp();
    // Mesmo breakpoint do shell: sem painel lateral ate 1024px, o filtro fica no topo da agenda.
    const isCompactViewport = useMediaQuery({ maxWidth: 1024 });

    const [agenda, setAgenda] = useState<AgendaEstabelecimentoDate[]>([]);
    const [waitlistEntries, setWaitlistEntries] = useState<any[]>([]);
    const [waitlistLoading, setWaitlistLoading] = useState(false);
    const [waitlistOpen, setWaitlistOpen] = useState(false);
    const [waitlistNudge, setWaitlistNudge] = useState(false);
    const [waitlistHidden, setWaitlistHidden] = useState(false);
    const hideTimerRef = useRef<number | null>(null);

    const HIDE_TTL_MS = 10 * 60 * 1000;

    const loadAgenda = useCallback(async () => {
        if (!attendantId) return;

        try {
            setLoading(true);

            const { data } = await atendentesService.getAgendaList(attendantId, moment(selectedDate).format('YYYY-MM-DD'));

            setAgenda(data);
        } catch (e) {
            console.error('Erro ao carregar a agenda:', e);
        } finally {
            setLoading(false);
        }
    }, [attendantId, selectedDate]);

    useEffect(() => {
        loadAgenda();
    }, [loadAgenda]);

    const selectedDateKey = useMemo(
        () => moment(selectedDate).format('YYYY-MM-DD'),
        [selectedDate]
    );

    const filterWaitlistByDate = (entries: any[]) => {
        const target = moment(selectedDate).startOf('day');
        return (entries || []).filter((entry) => {
            const start = entry?.preferredStartAt ? moment(entry.preferredStartAt).startOf('day') : null;
            const end = entry?.preferredEndAt ? moment(entry.preferredEndAt).startOf('day') : null;
            if (!start || !end) return false;
            return target.isBetween(start, end, undefined, '[]');
        });
    };

    const clearHideTimer = () => {
        if (hideTimerRef.current) {
            window.clearTimeout(hideTimerRef.current);
            hideTimerRef.current = null;
        }
    };

    const scheduleReopen = (ms: number) => {
        if (ms <= 0) return;
        clearHideTimer();
        hideTimerRef.current = window.setTimeout(() => {
            setWaitlistHidden(false);
            setWaitlistNudge(true);
            loadWaitlistForDay();
            setTimeout(() => setWaitlistNudge(false), 2000);
        }, ms);
    };

    const applyHideRule = (entries: any[]) => {
        if (!entries.length) {
            setWaitlistHidden(false);
            clearHideTimer();
            return;
        }

        const hideKey = `waitlist-hide:${selectedDateKey}`;
        const hiddenAtRaw = localStorage.getItem(hideKey);
        const hiddenAt = hiddenAtRaw ? Number(hiddenAtRaw) : 0;
        if (hiddenAt && Number.isFinite(hiddenAt)) {
            const diff = Date.now() - hiddenAt;
            if (diff < HIDE_TTL_MS) {
                setWaitlistHidden(true);
                scheduleReopen(HIDE_TTL_MS - diff);
                return;
            }
        }

        localStorage.removeItem(hideKey);
        setWaitlistHidden(false);
        clearHideTimer();
    };

    const loadWaitlistForDay = async () => {
        try {
            setWaitlistLoading(true);
            const { data } = await filaEsperaService.findAll();
            const filtered = filterWaitlistByDate(data || []);
            setWaitlistEntries(filtered);
            applyHideRule(filtered);
            return filtered;
        } catch (error) {
            console.error('Erro ao carregar fila de espera:', error);
            setWaitlistEntries([]);
            applyHideRule([]);
            return [];
        } finally {
            setWaitlistLoading(false);
        }
    };

    useEffect(() => {
        loadWaitlistForDay();
    }, [selectedDateKey]);

    useEffect(() => {
        const handler = () => {
            const hideKey = `waitlist-hide:${selectedDateKey}`;
            localStorage.removeItem(hideKey);
            setWaitlistHidden(false);
            setWaitlistNudge(true);
            loadWaitlistForDay();
            setTimeout(() => setWaitlistNudge(false), 2000);
        };

        window.addEventListener('waitlist:nudge', handler as EventListener);
        return () => {
            window.removeEventListener('waitlist:nudge', handler as EventListener);
        };
    }, [selectedDateKey]);

    return (
        <>
            {isCompactViewport && (user?.admin || user?.root || user?.worker) && (
                <div className="mb-3">
                    <FilterAgenda variant="compact" />
                </div>
            )}
            <div className="pb-[calc(4rem+env(safe-area-inset-bottom))] space-y-2">
                {loading ? (
                    <div className="flex justify-center"><Loading /></div>
                ) : (
                    <div>
                        {agenda.length > 0 ? (
                            agenda.map((agendaItem, index) => (
                                <AgendaProvider key={agendaItem?.date || index} date={agendaItem.date}>
                                    <DateItem agenda={agendaItem} index={index} />
                                </AgendaProvider>
                            ))
                        ) : (
                            <div className="text-center text-base-content/60">Nenhum agendamento para este dia.</div>
                        )}
                    </div>
                )}
            </div>

            {!!waitlistEntries.length && !waitlistHidden && (
                <div
                    className={`fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-6 z-40 max-w-xs rounded-2xl border border-base-300 bg-base-100 shadow-lg p-4 transition ${
                        waitlistNudge ? 'animate-pulse ring-2 ring-primary/40' : ''
                    }`}
                >
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-xs uppercase tracking-wide text-base-content/50">
                                Fila de espera
                            </div>
                            <div className="text-sm font-medium text-base-content">
                                {waitlistEntries.length} cliente{waitlistEntries.length > 1 ? 's' : ''} hoje
                            </div>
                        </div>
                        <button
                            type="button"
                            className="sf-close-button btn btn-ghost btn-xs"
                            onClick={() => {
                                const hideKey = `waitlist-hide:${selectedDateKey}`;
                                localStorage.setItem(hideKey, String(Date.now()));
                                setWaitlistHidden(true);
                                scheduleReopen(HIDE_TTL_MS);
                            }}
                            title="Fechar por 10 minutos"
                        >
                            <X aria-hidden="true" />
                        </button>
                    </div>
                    <div className="mt-3 flex items-center gap-2">
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={() => setWaitlistOpen(true)}
                        >
                            Ver lista
                        </button>
                        <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => navigate('/fila-espera')}
                        >
                            Ir para fila
                        </button>
                    </div>
                </div>
            )}

            <WaitlistAlertModal
                open={waitlistOpen}
                date={selectedDate}
                entries={waitlistEntries}
                loading={waitlistLoading}
                onClose={() => setWaitlistOpen(false)}
                onViewAll={() => {
                    setWaitlistOpen(false);
                    navigate('/fila-espera');
                }}
            />
        </>
    );
};
